import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Connect to backend API
    toast.success(t.contact.successMessage);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="dark-container pt-20">
      <SEO title="Contatti - ALLMX" description="Contattaci su WhatsApp, email o tramite il modulo di contatto. ALLMX - Ricambi e accessori motocross." url="https://allmx.it/contact" />
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="display-large mb-4">{t.contact.title}</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="display-medium mb-8">{t.contact.formTitle}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-lg font-medium mb-2">{t.contact.name}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                    style={{ borderRadius: '0px' }}
                    placeholder={t.contact.namePlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium mb-2">{t.contact.email}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                    style={{ borderRadius: '0px' }}
                    placeholder={t.contact.emailPlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium mb-2">{t.contact.subject}</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                    style={{ borderRadius: '0px' }}
                    placeholder={t.contact.subjectPlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium mb-2">{t.contact.message}</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-[var(--brand-primary)] transition-colors resize-none"
                    style={{ borderRadius: '0px' }}
                    placeholder={t.contact.messagePlaceholder}
                  />
                </div>

                <button type="submit" className="btn-primary w-full">
                  {t.contact.send} <Send size={20} />
                </button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div>
              <h2 className="display-medium mb-8">{t.contact.infoTitle}</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                      <MapPin size={24} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t.contact.location}</div>
                    <div className="body-medium">{t.contact.locationValue}</div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      38.13344198412488, 13.339307106685496
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                      <Mail size={24} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t.contact.emailLabel}</div>
                    <a href="mailto:info@allmx.it" className="body-medium hover:text-[var(--brand-primary)] transition-colors">
                      info@allmx.it
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                      <Phone size={24} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t.contact.support}</div>
                    <div className="body-medium">{t.contact.supportValue}</div>
                  </div>
                </div>

                <a
                  href="https://wa.me/393711289838"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-4 group p-4 -m-4 rounded-lg hover:bg-[#25D366]/10 transition-colors"
                  data-testid="whatsapp-contact"
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t.contact.whatsapp}</div>
                    <div className="body-medium group-hover:text-[#25D366] transition-colors">{t.contact.whatsappValue}</div>
                    <div className="text-sm font-mono mt-1" style={{ color: 'var(--text-muted)' }}>+39 371 128 9838</div>
                  </div>
                </a>
              </div>

              {/* Google Map */}
              <div className="dark-card overflow-hidden" style={{ height: '400px' }}>
                <iframe
                  src="https://www.google.com/maps?q=38.13344198412488,13.339307106685496&z=15&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  title="ALLMX Location - Palermo, Italy"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
